import { lotQueries } from '~/services/queries'

export const useLotAdmin = () => {
  const adminState = useState('lot-admin', () => ({
    items: {} as Record<string, unknown>,
    isLoading: false,
  }))

  const findLotInCollection = (lotId: number | string) => {
    const collection = (adminState.value.items as Record<string, unknown>)?.collection as Record<string, unknown>[] | undefined
    return collection?.find(item => item.id === lotId)
  }

  const items = computed(() => adminState.value.items)
  const lotsCollection = computed(() => (adminState.value.items as Record<string, unknown>)?.collection)
  const pageInfo = computed(() => (adminState.value.items as Record<string, unknown>)?.metadata)
  const isLoading = computed(() => adminState.value.isLoading)

  const setLots = (lots: Record<string, unknown>) => {
    adminState.value.items = lots
  }

  const startLoading = () => {
    adminState.value.isLoading = true
  }

  const finishLoading = () => {
    adminState.value.isLoading = false
  }

  const fetchlotsForAdmin = async ({ states, page }: { states: string[], page: number }) => {
    try {
      const { $gql } = useNuxtApp()
      startLoading()
      const { result } = await $gql.request(lotQueries.fetchlotsForAdmin, { states, page })
      finishLoading()
      setLots(result)
      return true
    } catch (error) {
      console.log(error)
      return false
    }
  }

  const fetchlotForAdminByLotId = async ({ id }: { id: number | string }) => {
    const { $gql } = useNuxtApp()
    startLoading()
    const { result } = await $gql.request(lotQueries.fetchlotForAdminByLotId, { id })
    finishLoading()
    setLots({ collection: [result] })
  }

  const fetchlotsForAdminByUserId = async ({
    id,
    page,
    states,
  }: {
    id: number | string
    page: number
    states: string[]
  }) => {
    const { $gql } = useNuxtApp()
    startLoading()
    const { result } = await $gql.request(lotQueries.fetchlotsForAdminByUserId, { id, page, states })
    finishLoading()
    setLots({ ...(result.lots as Record<string, unknown>) })
    return Boolean(result.lots)
  }

  const fetchLotsByTagsId = async ({
    page,
    tagId,
    states,
  }: {
    page: number
    tagId: number
    states: string[]
  }) => {
    const { $gql } = useNuxtApp()
    startLoading()
    const { result } = await $gql.request(lotQueries.fetchLotsByTagsId, { page, tagId, states })
    finishLoading()
    setLots({ ...(result.lots as Record<string, unknown>) })
  }

  const fetchLotOnCreated = async ({ id }: { id: number | string }) => {
    const { $gql } = useNuxtApp()
    startLoading()
    const { result } = await $gql.request(lotQueries.fetchLotOnCreated, { id })
    finishLoading()
    return result
  }

  const adminUpdateDepositLot = ({ lotId, deposit }: { lotId: number | string, deposit: unknown }) => {
    const result = findLotInCollection(lotId)
    if (result) {
      result.deposit = deposit
    }
  }

  const adminUpdateSearchRank = ({ lotId, searchRank }: { lotId: number | string, searchRank: unknown }) => {
    const result = findLotInCollection(lotId)
    if (result) {
      result.searchRank = searchRank
    }
  }

  const adminUpdatePartner = ({ lotId, partner }: { lotId: number | string, partner: unknown }) => {
    const result = findLotInCollection(lotId)
    if (result) {
      result.partner = partner
    }
  }

  const adminUpdateFeeCoefficient = ({
    lotId,
    ownerFeeCoefficient,
  }: {
    lotId: number | string
    ownerFeeCoefficient: unknown
  }) => {
    const result = findLotInCollection(lotId)
    if (result) {
      result.ownerFeeCoefficient = ownerFeeCoefficient
    }
  }

  const adminUpdateQuantity = ({ lotId, quantity }: { lotId: number | string, quantity: unknown }) => {
    const result = findLotInCollection(lotId)
    if (result) {
      result.quantity = quantity
    }
  }

  const adminHideLot = (id: number | string) => {
    const result = findLotInCollection(id)
    if (result) {
      result.hidden = true
    }
  }

  const adminShowLot = (id: number | string) => {
    const result = findLotInCollection(id)
    if (result) {
      result.hidden = false
    }
  }

  const adminDeleteLot = (id: number | string) => {
    const result = findLotInCollection(id)
    if (result) {
      result.deleted = true
    }
  }

  const adminDoNotDelete = (id: number | string) => {
    const result = findLotInCollection(id)
    if (result) {
      result.doNotRemove = true
    }
  }

  const adminUpdatePrices = ({ id, prices }: { id: number | string, prices: unknown }) => {
    const result = findLotInCollection(id)
    if (result) {
      result.prices = prices
    }
  }

  const adminSetState = ({ lotState, id }: { lotState: unknown, id: number | string }) => {
    const result = findLotInCollection(id)
    if (result) {
      result.state = lotState
    }
  }

  const adminRemoveTagFromLot = ({ tagId, lotId }: { tagId: number | string, lotId: number | string }) => {
    const result = findLotInCollection(lotId)
    if (result) {
      const tags = result.tags as Record<string, unknown>[]
      result.tags = tags.filter(tag => tag.id !== tagId)
    }
  }

  const adminAddTagFromLot = ({ tag, lotId }: { tag: Record<string, unknown>, lotId: number | string }) => {
    const result = findLotInCollection(lotId)
    if (result) {
      const tags = (result.tags as Record<string, unknown>[]) || []
      if (!tags.find(item => item.id === tag.id)) {
        tags.push(tag)
      }
      result.tags = tags
    }
  }

  return {
    items,
    lotsCollection,
    pageInfo,
    isLoading,
    fetchlotsForAdmin,
    fetchlotForAdminByLotId,
    fetchlotsForAdminByUserId,
    fetchLotsByTagsId,
    fetchLotOnCreated,
    adminUpdateDepositLot,
    adminUpdateSearchRank,
    adminUpdatePartner,
    adminUpdateFeeCoefficient,
    adminUpdateQuantity,
    adminHideLot,
    adminShowLot,
    adminDeleteLot,
    adminDoNotDelete,
    adminUpdatePrices,
    adminSetState,
    adminRemoveTagFromLot,
    adminAddTagFromLot,
  }
}

export const useLot = (lotId: number | string) => {
  const { $gql } = useNuxtApp()
  const { startSubmitting, finishSubmitting } = useStoreFields()

  const state = useState(`lot-${lotId}`, () => ({
    id: null,
    items: {},
    item: {
      prices: {},
    },
    isLoading: false,
    priceDetails: {
      delivery: 0,
      deliveryPrice: 0,
      deposit: 0,
      ownerServiceFee: 0,
      rent: 0,
      renterServiceFee: 0,
      insurance: 0,
      insurancePrice: 0,
      totalPrice: 0,
      totalReward: 0,
      bookingPayment: 0,
      cashPayment: 0,
    },
    isPriceDetailsInitial: false,
    deliveryFlag: false,
    hasSelectedDate: false,
    rentalDays: 1,
  }))

  const item = computed(() => state.value.item)
  const lot = computed(() => state.value.item)
  const currency = computed(() => state.value.item.currency || 'RUB')
  const priceDetails = computed(() => state.value.priceDetails)
  const isLoading = computed(() => state.value.isLoading)
  const isUnavailable = computed(() =>
    state.value.item?.hidden || state.value.item?.deleted,
  )
  const isPriceDetailsInitial = computed(() => state.value.isPriceDetailsInitial)

  const setFavorite = (isFavorite: boolean) => {
    state.value.item.isFavorite = isFavorite
  }

  const pricesWithFee = computed(() => {
    if (!state.value.item?.prices) return {}

    const { daily, weekly, monthly } = state.value.item?.prices || {}
    const coefficient = state.value.item?.renterFeeCoefficient || 0

    return {
      daily: Math.round(10 * daily * (1 + coefficient)) / 10,
      weekly: Math.round((10 * weekly * (1 + coefficient)) / 7) / 10,
      monthly: Math.round((10 * monthly * (1 + coefficient)) / 30) / 10,
    }
  })

  const setLot = (nextLot) => {
    state.value.item = nextLot
  }

  const startLoading = () => state.value.isLoading = true
  const finishLoading = () => state.value.isLoading = false

  const initDefault = () => {
    state.value.deliveryFlag = false
    state.value.hasSelectedDate = false
    state.value.rentalDays = 1
  }

  const calculatePrice = async (payload) => {
    startLoading()

    startSubmitting(lotId)

    const { result } = await $gql.request(lotQueries.calculatePrice, {
      lotId,
      dateFrom: payload.dateFrom,
      dateTo: payload.dateTo,
      deliveryFlag: payload.deliveryFlag,
      insuranceFlag: payload.insuranceFlag,
      rentHours: payload.rentHours,
    })
    finishLoading()

    state.value.priceDetails = result

    finishSubmitting(lotId)
    state.value.isPriceDetailsInitial = true

    return result
  }

  return {
    state,
    item,
    lot,
    isUnavailable,
    pricesWithFee,
    currency,
    priceDetails,
    isLoading,
    isPriceDetailsInitial,
    setLot,
    startLoading,
    finishLoading,
    initDefault,
    calculatePrice,
    setFavorite,
  }
}
